import React, { FC, useMemo } from 'react';
import classNames from 'classnames';
import { useFormikContext } from 'formik';

import { LoadingPage } from 'Shared/components';
import {
  PaymentDetailsType,
  PaymentPlanType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext } from 'QuoteApp/services/providers';

import { RewardsEstimate } from './RewardsEstimate';
import { FlexiblePaymentSidebar } from './FlexiblePaymentSidebar';
import { StandardPaymentSidebar } from './StandardPaymentSidebar';
import { PurchaseFormValuesType } from '../types';
import styles from './styles.module.css';

type SidebarContentProps = {
  paymentDetails: PaymentDetailsType;
};

export const SidebarContent: FC<SidebarContentProps> = ({ paymentDetails }) => {
  const { config, details, rewardsEnrollmentResponse, rate } = useAppContext();
  const isSweepPurchase = !!config?.sweepEnabled;
  const { values } = useFormikContext<PurchaseFormValuesType>();

  const isPaymentFlexibilityEnabled = !!config?.flexiblePayments.enabled;

  const currentPlan = paymentDetails.paymentPlans.find(
    plan => plan.identifier === values.paymentPlanId
  );

  const isFlexiblePaymentPlan =
    isPaymentFlexibilityEnabled && currentPlan?.paymentPlanEnum === 'flexible';

  const includedDrivers = useMemo(
    () =>
      Object.keys(details.drivers).reduce((acc: Array<string>, d: string) => {
        if (details.drivers[d].driverType !== 'excludedPerson') {
          acc.push(d);
        }
        return acc;
      }, []).length,
    [details]
  );

  const excludedDrivers = useMemo(
    () =>
      Object.keys(details.drivers).reduce((acc: Array<string>, d: string) => {
        if (details.drivers[d].driverType === 'excludedPerson') {
          acc.push(d);
        }
        return acc;
      }, []).length,
    [details]
  );

  const renderSidebarContent = (paymentPlan: PaymentPlanType) => {
    if (isFlexiblePaymentPlan) {
      return (
        <FlexiblePaymentSidebar
          paymentPlan={paymentPlan}
          excludedDrivers={excludedDrivers}
          includedDrivers={includedDrivers}
        />
      );
    }

    return (
      <StandardPaymentSidebar
        paymentPlan={paymentPlan}
        excludedDrivers={excludedDrivers}
        includedDrivers={includedDrivers}
      />
    );
  };

  return (
    <div
      className={classNames(
        styles.sidebarWrapper,
        isSweepPurchase ? styles.noTopOffset : ''
      )}
    >
      <div className={styles.sidebar}>
        {!rate || !currentPlan ? (
          <LoadingPage />
        ) : (
          renderSidebarContent(currentPlan)
        )}
      </div>

      {rewardsEnrollmentResponse?.isEnrolled && <RewardsEstimate />}
    </div>
  );
};
